/**
 * O traço do diagrama.
 *
 * **O que se vê é o desenho gravado**, que é de onde o site do cliente serve.
 * Não é uma aproximação nossa: é o desenho publicado.
 *
 * **E a tela diz quando ele está velho.** Toda escrita da edição marca o diagrama
 * para redesenhar — 655 dos 4.829 estão assim. Mostrar sem avisar seria pior do
 * que não mostrar.
 *
 * **Redesenhar agora é daqui.** O motor é o `Desenhar()` da `BibliotecaCore`, o
 * mesmo que serve o site. Conferir roda o motor inteiro **sem gravar** e diz em
 * que classe cai a diferença; gravar muda o que o site serve, e por isso é o
 * segundo botão.
 *
 * **Carrega sob demanda.** São 89 kB em média e 523 kB no maior; ninguém precisa
 * pagar isso ao abrir a ficha. O estado (tem / está velho / quanto pesa) vem numa
 * consulta barata, e o desenho só quando se pede.
 */

import { useEffect, useState } from "react"
import {
  baixarDesenho, baixarPreviaDoDesenho, usarConferirRedesenho, usarEstadoDoDesenho,
  usarRedesenhar,
  type DesfechoDoRedesenho, type RedesenhoDoDiagrama,
} from "@/api/desenhoDoDiagrama"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

/** A cor do veredito: só a última classe é problema. */
const COR: Record<DesfechoDoRedesenho, string> = {
  Igual: "var(--ok, #157f3d)",
  UltimoDigito: "var(--ok, #157f3d)",
  Numero: "var(--aviso, #b45309)",
  Estrutura: "var(--aviso, #b45309)",
}

const ROTULO: Record<DesfechoDoRedesenho, string> = {
  Igual: "idêntico ao gravado",
  UltimoDigito: "igual, a menos do último dígito",
  Numero: "coordenada diferente",
  Estrutura: "estrutura ou texto diferente",
}

export function DesenhoDoDiagrama({ projetoId, diagramaId }: {
  projetoId: number
  diagramaId: number
}) {
  const { data: estado, isPending } = usarEstadoDoDesenho(diagramaId)

  const [url, setUrl] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // O desenho tem ~800px de largura e a ficha e um painel estreito. Caber por
  // padrao mostra a forma; o tamanho real e para quando se quer ler o texto dos
  // terminais, e ai rolar e o comportamento certo.
  const [tamanhoReal, setTamanhoReal] = useState(false)

  const conferir = usarConferirRedesenho(diagramaId)
  const redesenhar = usarRedesenhar(projetoId, diagramaId)
  const [veredito, setVeredito] = useState<RedesenhoDoDiagrama | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  const [previa, setPrevia] = useState<string | null>(null)
  const [desenhandoPrevia, setDesenhandoPrevia] = useState(false)

  // O mesmo cuidado do desenho gravado: cada previa e outro blob de ~250 kB.
  useEffect(() => {
    return () => { if (previa) URL.revokeObjectURL(previa) }
  }, [previa])

  async function verPrevia() {
    setDesenhandoPrevia(true); setRecado(null)
    try {
      setPrevia(await baixarPreviaDoDesenho(diagramaId))
    } catch (e) {
      setRecado(e instanceof ErroDaApi ? e.message : 'Não foi possível desenhar.')
    } finally {
      setDesenhandoPrevia(false)
    }
  }

  function rodar(qual: "conferir" | "gravar") {
    setRecado(null); setVeredito(null)
    const m = qual === "conferir" ? conferir : redesenhar
    m.mutate(undefined, {
      onSuccess: (r) => {
        setVeredito(r)
        // O traco mudou: o blob antigo nao vale mais.
        if (r.gravou) { setUrl(null) }
      },
      onError: (e) => setRecado(
        e instanceof ErroDeTrava || e instanceof ErroDaApi
          ? e.message
          : "Não foi possível redesenhar."),
    })
  }

  // Trocar de diagrama solta o blob do anterior: sem isto cada diagrama aberto
  // deixa 89 kB pendurados na aba ate ela fechar.
  useEffect(() => {
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [url])

  useEffect(() => {
    setUrl(null); setErro(null); setVeredito(null); setRecado(null); setPrevia(null)
  }, [diagramaId])

  async function abrir() {
    setCarregando(true); setErro(null)
    try {
      setUrl(await baixarDesenho(diagramaId))
    } catch (e) {
      setErro(e instanceof ErroDaApi ? e.message : "Não foi possível carregar o desenho.")
    } finally {
      setCarregando(false)
    }
  }

  if (isPending) return null

  return (
    <div style={{
      marginTop: 12, padding: 11, borderRadius: 8,
      border: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <b style={{ fontSize: 12 }}>Desenho</b>

        {!estado?.tem ? (
          <span style={{ fontSize: 11, color: "var(--txt3)" }}>
            Este diagrama nunca foi desenhado — use Redesenhar e gravar, aqui embaixo.
          </span>
        ) : (
          <>
            <span style={{ fontSize: 11, color: "var(--txt3)" }}>
              {(estado.tamanho / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kB
            </span>
            {!url && (
              <button
                onClick={abrir}
                disabled={carregando}
                style={{
                  height: 24, padding: "0 10px", borderRadius: 6, fontSize: 11.5,
                  border: "1px solid var(--line)", background: "var(--surface)",
                  color: "var(--txt2)", cursor: carregando ? "default" : "pointer",
                }}
              >
                {carregando ? "Carregando…" : "Ver o desenho"}
              </button>
            )}
            {url && (
              <>
                <button
                  onClick={() => setTamanhoReal((t) => !t)}
                  style={{
                    height: 24, padding: "0 10px", borderRadius: 6, fontSize: 11.5,
                    border: "1px solid var(--line)", background: "var(--surface)",
                    color: "var(--txt2)", cursor: "pointer",
                  }}
                >
                  {tamanhoReal ? "caber na largura" : "tamanho real"}
                </button>
                <a href={url} target="_blank" rel="noreferrer"
                   style={{ fontSize: 11.5, color: "var(--brand-on-soft)" }}>
                  abrir em outra aba
                </a>
              </>
            )}
          </>
        )}
      </div>

      {estado?.tem && estado.desatualizado && (
        <p style={{
          margin: "8px 0 0", padding: "6px 9px", borderRadius: 5, fontSize: 11, lineHeight: 1.5,
          background: "color-mix(in srgb, var(--aviso, #b45309) 12%, transparent)",
          border: "1px solid var(--aviso, #b45309)", color: "var(--txt2)",
        }}>
          <b>Este desenho é anterior às últimas edições.</b> O diagrama está marcado para
          redesenhar{estado.data ? ` desde ${new Date(estado.data).toLocaleString("pt-BR")}` : ""} —
          o traço abaixo é o que o site serve agora, não o que o dado diz hoje.
        </p>
      )}

      {erro && (
        <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--erro)" }}>{erro}</p>
      )}

      <div style={{
        marginTop: 9, paddingTop: 9, borderTop: "1px solid var(--line2)",
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <b style={{ fontSize: 11.5 }}>Redesenhar</b>

        <button
          onClick={() => rodar("conferir")}
          disabled={conferir.isPending || redesenhar.isPending}
          style={{
            height: 24, padding: "0 10px", borderRadius: 6, fontSize: 11.5,
            border: "1px solid var(--line)", background: "var(--surface)", color: "var(--txt2)",
          }}
        >
          {conferir.isPending ? "Desenhando…" : "Conferir sem gravar"}
        </button>

        <button
          onClick={() => rodar("gravar")}
          disabled={conferir.isPending || redesenhar.isPending}
          style={{
            height: 24, padding: "0 10px", borderRadius: 6, fontSize: 11.5,
            border: "1px solid var(--line)", background: "var(--surface)", color: "var(--txt2)",
          }}
        >
          {redesenhar.isPending ? "Gravando…" : "Redesenhar e gravar"}
        </button>

        <button
          onClick={verPrevia}
          disabled={desenhandoPrevia || conferir.isPending || redesenhar.isPending}
          style={{
            height: 24, padding: "0 10px", borderRadius: 6, fontSize: 11.5,
            border: "1px solid var(--line)", background: "var(--surface)", color: "var(--txt2)",
          }}
        >
          {desenhandoPrevia ? "Desenhando…" : previa ? "desenhar de novo" : "Ver como ficaria"}
        </button>

        <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
          monta o diagrama inteiro — de 1 a 60 s
        </span>
      </div>

      {recado && (
        <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--erro)" }}>{recado}</p>
      )}

      {veredito && (
        <p style={{
          margin: "8px 0 0", padding: "6px 9px", borderRadius: 5, fontSize: 11, lineHeight: 1.5,
          border: `1px solid ${COR[veredito.veredito.desfecho]}`,
          background: `color-mix(in srgb, ${COR[veredito.veredito.desfecho]} 10%, transparent)`,
          color: "var(--txt2)",
        }}>
          <b>{veredito.gravou ? "Gravado" : "Conferido"}
            {" — "}{ROTULO[veredito.veredito.desfecho]}.</b>{" "}
          {veredito.veredito.explicacao}{" "}
          {veredito.veredito.pista && (
            <code style={{ fontSize: 10.5 }}>{veredito.veredito.pista}</code>
          )}
          <br />
          <span style={{ color: "var(--txt3)" }}>
            {veredito.veredito.bytesGravado.toLocaleString("pt-BR")} bytes gravados contra{" "}
            {veredito.veredito.bytesGerado.toLocaleString("pt-BR")} gerados, em{" "}
            {(veredito.milissegundos / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} s.
          </span>
        </p>
      )}

      {url && (
        <div style={{
          marginTop: 9, padding: 8, borderRadius: 6, background: "#FFFFFF",
          maxHeight: "70vh", overflow: "auto",
        }}>
          {/* <img> e nao innerHTML: dentro de uma imagem, o SVG nao roda script — e
              o desenho vem do banco, com o que quer que tenha sido gravado nele. */}
          <img
            src={url}
            alt="Desenho gravado do diagrama"
            style={{ display: "block", maxWidth: tamanhoReal ? "none" : "100%" }}
          />
        </div>
      )}

      {previa && (
        <>
          <p style={{ margin: "10px 0 0", fontSize: 11, color: "var(--txt3)" }}>
            <b style={{ color: "var(--txt2)" }}>Como ficaria.</b> Este traço não está gravado —
            é o que o motor desenha a partir do dado de agora.
          </p>
          <div style={{
            marginTop: 6, padding: 8, borderRadius: 6, background: "#FFFFFF",
            maxHeight: "70vh", overflow: "auto",
            outline: "2px solid var(--aviso, #b45309)", outlineOffset: -1,
          }}>
            <img
              src={previa}
              alt="Desenho do diagrama como ficaria"
              style={{ display: "block", maxWidth: tamanhoReal ? "none" : "100%" }}
            />
          </div>
        </>
      )}
    </div>
  )
}
